<template>
  <div @copy="captureCopy">
    <slot/>

    <LimitActionModal :show.sync="showBlocker" :action-permissions="copyPermissions" />
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'
import { debouncer } from '@/util/debouncer'
import LimitActionModal from '@/components/LimitActionModal'
import { getSelectedText } from '@/util/copypaste_helpers'
import { actionPermissions, ACTION_TYPES } from '@/util/access_visibility'

export default {
  name: 'CopyTrackWrapper',
  components: {
    LimitActionModal
  },
  props: {
    type: {
      type: String,
      default: ''
    },
    title: {
      type: String,
      default: ''
    },
    uuid: {
      type: String,
      default: ''
    },
    level: {
      type: String,
      default: ''
    },
    demo: {
      type: Boolean,
      default: false
    },
    enforceQuota: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      text: '',
      timeOfLastCopy: 0,
      debounceTime: 500,
      showBlocker: false
    }
  },
  computed: {
    ...mapGetters('user', ['authInfo', 'userId']),
    ...mapGetters('contentAccess', ['contentAccessStatus', 'copyCount']),
    copyPermissions() {
      return actionPermissions(
        ACTION_TYPES.COPY,
        this.type,
        { demo: this.demo, uuid: this.uuid, title: this.title },
        this.authInfo,
        this.contentAccessStatus
      )
    },
    truncatedText() {
      let truncatedText = this.text
      let words = this.text.split(' ')
      if (words.length > 40) {
        truncatedText = words
          .slice(0, 40)
          .concat([`...(${words.length - 40} more words)`])
          .join(' ')
      }
      return truncatedText
    }
  },
  methods: {
    ...mapMutations('contentAccess', ['incrementCopyCount']),
    ...mapActions('contentAccess', ['refetchContentAccessReport']),
    captureCopy(evt) {
      let now = Date.now()
      let lastCopy = this.timeOfLastCopy
      this.timeOfLastCopy = now

      // debounce handler: if last copy was within the debounceTime, skip
      if (now - lastCopy < this.debounceTime) {
        evt.preventDefault()
        evt.stopPropagation()
        return
      }

      // check permissions
      if (this.copyPermissions.block) {
        evt.preventDefault()
        console.info('copy prevented')
        evt.stopPropagation()

        // If user is blocked because of exceeding their quota, we show a modal which will automatically send a content access blocked event
        // if there's a different reason, we block the copy silently
        if (this.enforceQuota && this.copyPermissions.blockQuota) {
          this.showBlocker = true
        }
        return
      }
      this.text = getSelectedText()

      if (this.enforceQuota) {
        this.incrementCopyCount()
        this.refetchContentAccessReport()
      }

      const evtData = {
        content_access_report_error: this.contentAccessStatus.reportError,
        copy_count: this.copyCount,
        copied_text_type: this.type,
        copied_text_text: this.truncatedText,
        copied_text_title: this.title,
        copied_text_uuid: this.uuid,
        copied_text_level: this.level,
        from_button: !!(evt.detail && evt.detail.fromButton),
        //Deprecate these
        type: this.type,
        title: this.title,
        uuid: this.uuid,
        level: this.level,
        // cap the # of words we send with event
        text: this.truncatedText,
        ...(this.type === 'objective' && {obj_level: this.level})
      }

      this.$sendEvent('Copied Text', evtData)
      evt.stopPropagation()
    }
  }
}
</script>
